import React, { Dispatch, JSX, SetStateAction } from "react";
import produce from "immer";
import { Box, Stack, Typography } from "@mui/material";
import QuestionnaireResponse from "../Questionnaire";
import Vitals from "../Vitals";
import Symptoms from "../Symptoms";
import PastHistoryOfAllergy from "../PastHistoryOfAllergy";
import Status from "../Status";
import Hopi from "../HOPI";
import Investigation from "../Investigation";
import IntraOral from "../IntraOral/IntraOral";
import {
  Assessment as AssessmentInterface,
  IntraOralValue,
  SkinAnalysisAttribute
} from "../../../interfaces/AssessmentInterfaces";
import ExtraOral, { ExtraOralData } from "../ExtraOral";
import IntraOralAdvanced from "../IntraOralAdvanced";
import EyeExamination from "../EyeExamination/EyeExamination";
import ProvisionalDiagnosis from "../ProvisionalDiagnosis";
import LabTest from "../LabTest";
import LabService from "../LabServices/LabService";
import PresentHealthStatus from "../PresentHealthStatus";
import Attachment from "../Attachment";
import EditableTable, { TableState } from "../../../components/EditableTable/EditableTable";
import TreatmentPlan from "../TreatmentPlan";
import TreatmentGiven from "../TreatmentGiven";
import Medication from "../Medication/Medication";
import ProductMedication from "../ProductMedication/ProductMedication";
import FollowUp from "../FollowUp";
import Advice from "../Advice/Advice";
import Others from "../Others";
import { ReverseMap } from "../../../helpers/types";
import useAssessmentLabel from "../../../hooks/useAssessmentLabel";
import { advancedIntraOralInitState } from "./Form";
import RecordAudio from "./RecordAudio";
import RecordAudioErrorBoundary from "./RecordAudioErrorBoundary";
import DiagnosisWithTreeView from "../Diagnosis/DiagnosisWithTreeView";
import Compare from "./Compare";
import OtherServicesSelector from "../OtherServicesSelector";
import TreatmentDone from "../TreatmentDone";
import { DentalServices } from "../DentalServices";
import { XrayServices } from "../XrayServices";
import { Dermascopy } from "../Dermascopy";
import { Other1 } from "../Other1";
import { Other2 } from "../Other2";
import { Other3 } from "../Other3";
import useCurrentAuthenticable from "../../../hooks/useCurrentAuthenticable";
import useHasTranscriptionEnabled from "../../../hooks/useHasTranscriptionEnabled";
import { SkinAnalysis } from "../Skin/SkinAnalysis";

// @ts-expect-error - css
import styles from "../Assessment.module.css";
import DiagnosisList from "../Diagnosis/DiagnosisList";

export enum SORT_ORDER {
  RECORD_PRESCRIPTION = "recordPrescription",
  VITALS = "vitals",
  QUESTIONNAIRE = "questionnaire",
  CHIEF_COMPLAINTS = "chiefComplaints",
  PAST_HISTORY_OF_ALLERGY = "pastHistoryOfAllergy",
  EXAMINATION_OR_STATUS = "examination",
  HOPI = "hopi",
  INVESTIGATION = "investigation",
  COMPARE = "compare",
  INTRA_ORAL = "intraoral",
  EXTRA_ORAL = "extraOral",
  ADVANCED_INTRA_ORAL = "advancedIntraOral",
  EYE = "eye",
  PROVISIONAL_DIAGNOSIS = "provisionalDiagnosis",
  LAB_TEST = "labTest",
  SERVICE = "service",
  DIAGNOSIS = "diagnosis",
  PRESENT_HEALTH_STATUS = "presentHealthStatus",
  ATTACHMENTS = "attachments",
  TABULAR = "tabular",
  TREATMENT_PLAN = "treatmentPlan",
  TREATMENT_GIVEN = "treatmentGiven",
  TREATMENT_DONE = "treatmentDone",
  MEDICATION = "medication",
  PRODUCT_MEDICATION = "productMedication",
  FOLLOWUP = "followup",
  ADVICE = "advice",
  OTHERS = "others",
  OTHER_SERVICES = "otherServices",
  DENTAL_SERVICES = "dentalServices",
  XRAY_SERVICES = "xrayServices",
  DERMASCOPY = "dermascopy",
  OTHER1 = "other1",
  OTHER2 = "other2",
  OTHER3 = "other3",
  SKIN_ANALYSIS = "skinAnalysis",
  AUTHORIZED_SIGNERS = "authorizedSigners"
}

export type SortOrderType = ReverseMap<typeof SORT_ORDER>;

interface Props {
  component: SortOrderType;
  assessment: AssessmentInterface & { status: string };
  setAssessment: (value: Partial<AssessmentInterface> & { status: string }) => void;
  spID: number;
  assessmentId: number;
  themeColor: string;
  tabular: { initValue: TableState; title: string };
  clientId?: number;
  intraOral: {
    showFields: { permanent: boolean; primary: boolean };
    setShowFields: Dispatch<SetStateAction<{ permanent: boolean; primary: boolean }>>;
  };
}

const OPDComponents = ({
  component,
  assessment,
  setAssessment,
  spID,
  assessmentId,
  themeColor,
  tabular,
  intraOral,
  clientId
}: Props): JSX.Element => {
  const assessmentLabels = useAssessmentLabel();
  const { serviceProvider } = useCurrentAuthenticable();
  const { isTranscriptionEnabled } = useHasTranscriptionEnabled();

  return (
    <>
      {component === SORT_ORDER.RECORD_PRESCRIPTION && (
        <RecordAudioErrorBoundary>
          <RecordAudio
            value={assessment.prescriptionAudioUrl}
            onChange={(v) => {
              setAssessment((prev) => ({ ...prev, prescriptionAudioUrl: v }));
            }}
            transcript={assessment.prescriptionAudioTranscription || ""}
            onTranscriptChange={(v) => {
              setAssessment((prev) => ({ ...prev, prescriptionAudioTranscription: v }));
            }}
            summary={assessment.prescriptionAudioSummary || ""}
            onSummaryChange={(v) => {
              setAssessment((prev) => ({ ...prev, prescriptionAudioSummary: v }));
            }}
            transcriptionEnabled={isTranscriptionEnabled}
          />
        </RecordAudioErrorBoundary>
      )}

      {component === SORT_ORDER.COMPARE && <Compare data={assessment} clientId={clientId} />}

      {component === SORT_ORDER.QUESTIONNAIRE && (
        <QuestionnaireResponse
          assessmentQuestionnaire={assessment.questionnaire}
          onChange={(v) => {
            setAssessment({ ...assessment, questionnaire: v });
          }}
        />
      )}

      {component === SORT_ORDER.VITALS && (
        <Vitals
          assessmentVitals={assessment.vitals}
          onChange={(v) => {
            setAssessment({ ...assessment, vitals: v });
          }}
        />
      )}

      {component === SORT_ORDER.CHIEF_COMPLAINTS && (
        <Symptoms
          assessmentSymptoms={assessment.symptoms}
          onChange={(v) => {
            setAssessment({ ...assessment, symptoms: v });
          }}
        />
      )}

      {component === SORT_ORDER.PAST_HISTORY_OF_ALLERGY && (
        <PastHistoryOfAllergy
          assessmentPastHistoryOfAllergy={assessment?.pastHistoryOfAllergy}
          onChange={(v) => setAssessment({ ...assessment, pastHistoryOfAllergy: v })}
        />
      )}

      {/* status is not saved in database, might have to remove it */}
      {component === SORT_ORDER.EXAMINATION_OR_STATUS && (
        <Status
          status={assessment.status}
          assessmentImage={assessment.assessmentImage}
          onChange={(status) => setAssessment({ ...assessment, status })}
          onChangeImage={(image) => setAssessment({ ...assessment, assessmentImage: image })}
        />
      )}

      {component === SORT_ORDER.HOPI && (
        <Hopi
          assessmentHOPI={assessment.hopi}
          onChange={(v) => {
            setAssessment({ ...assessment, hopi: v });
          }}
        />
      )}

      {component === SORT_ORDER.INVESTIGATION && (
        <Investigation
          investigation={assessment.investigation}
          onChange={(investigation) => setAssessment({ ...assessment, investigation })}
        />
      )}

      {component === SORT_ORDER.INTRA_ORAL && (
        <Box className={styles.assessmentRow}>
          <Box className={styles.assessmentLabel}>
            <Typography component="span" fontWeight="600">
              {assessmentLabels.intraoral}
            </Typography>
          </Box>
          <Box className={styles.assessmentField}>
            <IntraOral
              themeColor={themeColor}
              type="permanent"
              numbering="normal"
              showField={intraOral.showFields.permanent}
              onAddClicked={() => {
                intraOral.setShowFields({ ...intraOral.showFields, permanent: true });
              }}
              onClearClicked={() => {
                intraOral.setShowFields({ ...intraOral.showFields, permanent: false });
              }}
              stateValue={assessment.intraOral}
              onNumberSave={(comboStrings: string | Array<string>, data: IntraOralValue) => {
                const keys = Array.isArray(comboStrings) ? comboStrings : [comboStrings];
                const newData = produce(assessment, (draft: AssessmentInterface) => {
                  keys.forEach((key) => {
                    draft.intraOral.permanent[key] = data;
                  });
                });

                setAssessment(newData);
              }}
              onNodeDelete={(comboStrings: string | Array<string>) => {
                const keys = Array.isArray(comboStrings) ? comboStrings : [comboStrings];
                const newData = produce(assessment, (draft: AssessmentInterface) => {
                  keys.forEach((key) => {
                    delete draft.intraOral.permanent[key];
                  });
                });

                setAssessment(newData);
              }}
            />

            <IntraOral
              themeColor={themeColor}
              type="primary"
              numbering="alphabets"
              showField={intraOral.showFields.primary}
              onAddClicked={() => {
                intraOral.setShowFields({ ...intraOral.showFields, primary: true });
              }}
              onClearClicked={() => {
                intraOral.setShowFields({ ...intraOral.showFields, primary: false });
              }}
              stateValue={assessment.intraOral}
              onNumberSave={(comboStrings: string | Array<string>, data: IntraOralValue) => {
                const keys = Array.isArray(comboStrings) ? comboStrings : [comboStrings];
                const newData = produce(assessment, (draft: AssessmentInterface) => {
                  keys.forEach((key) => {
                    draft.intraOral.primary[key] = data;
                  });
                });

                setAssessment(newData);
              }}
              onNodeDelete={(comboStrings: string | Array<string>) => {
                const keys = Array.isArray(comboStrings) ? comboStrings : [comboStrings];
                const newData = produce(assessment, (draft: AssessmentInterface) => {
                  keys.forEach((key) => {
                    delete draft.intraOral.primary[key];
                  });
                });

                setAssessment(newData);
              }}
            />
          </Box>
        </Box>
      )}

      {component === SORT_ORDER.EXTRA_ORAL && (
        <ExtraOral
          extraOralData={assessment.extraOral}
          onChange={(v: ExtraOralData) => {
            setAssessment({ ...assessment, extraOral: v });
          }}
        />
      )}

      {component === SORT_ORDER.ADVANCED_INTRA_ORAL && (
        <IntraOralAdvanced
          data={assessment.advancedIntraOral}
          onChange={(v) => setAssessment({ ...assessment, advancedIntraOral: v })}
          onCancel={() => {
            setAssessment({ ...assessment, advancedIntraOral: advancedIntraOralInitState });
          }}
        />
      )}

      {component === SORT_ORDER.EYE && (
        <EyeExamination
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          assessmentEyeExamination={assessment?.eye as any}
          onChange={(v) => {
            setAssessment({ ...assessment, eye: v });
          }}
        />
      )}

      {component === SORT_ORDER.PROVISIONAL_DIAGNOSIS && (
        <ProvisionalDiagnosis
          assessmentProvisionalDiagnosis={assessment.provisionalDiagnosis}
          onChange={(v) => {
            setAssessment({ ...assessment, provisionalDiagnosis: v });
          }}
        />
      )}

      {component === SORT_ORDER.LAB_TEST && (
        <LabTest
          assessmentLabTests={assessment.tests}
          onChange={(v) => {
            setAssessment({ ...assessment, tests: v });
          }}
        />
      )}

      {component === SORT_ORDER.SERVICE && (
        <LabService
          assessmentId={assessmentId || null}
          assessmentServices={assessment.services}
          onChange={(v) => {
            setAssessment({ ...assessment, services: [...assessment.services, v] });
          }}
          onRemove={(v) => {
            setAssessment({ ...assessment, services: v });
          }}
        />
      )}

      {component === SORT_ORDER.OTHER_SERVICES && (
        <OtherServicesSelector
          otherServices={assessment.otherServices}
          handleAddService={(v) => {
            setAssessment({
              ...assessment,
              otherServices: [
                ...assessment.otherServices,
                {
                  id: v.id,
                  name: v.name,
                  grossTotalPrice: v.grossTotalPrice,
                  document: v.document,
                  isMultiSession: v.isMultiSession,
                  category: v.category
                }
              ]
            });
          }}
          handleRemoveService={(v) => {
            setAssessment({
              ...assessment,
              otherServices: v
            });
          }}
        />
      )}

      {component === SORT_ORDER.DENTAL_SERVICES && (
        <DentalServices
          intraOral={assessment.intraOral}
          dentalServices={assessment.dentalServices || []}
          treatmentDone={assessment.treatmentDone}
          handleAddService={(v) => {
            setAssessment({
              ...assessment,
              dentalServices: [
                ...assessment.dentalServices,
                {
                  id: v.id,
                  name: v.name,
                  note: "",
                  teeth: [],
                  grossTotalPrice: v.grossTotalPrice,
                  document: v.document,
                  isMultiSession: v.isMultiSession,
                  category: v.category,
                  ...(serviceProvider ? { serviceProviderId: serviceProvider.id } : {})
                }
              ],
              treatmentDone: [
                ...assessment.treatmentDone,
                {
                  isBilled: false,
                  checked: false,
                  service: {
                    id: v.id,
                    name: v.name,
                    teeth: [],
                    grossTotalPrice: v.grossTotalPrice,
                    document: v.document,
                    isMultiSession: v.isMultiSession,
                    category: v.category,
                    ...(serviceProvider ? { serviceProviderId: serviceProvider.id } : {})
                  }
                }
              ]
            });
          }}
          handleUpdateService={(v) => {
            setAssessment({
              ...assessment,
              dentalServices: assessment.dentalServices.map((item) => {
                if (item.id === v.id) {
                  return {
                    id: v.id,
                    name: v.name,
                    note: v.note,
                    teeth: v.teeth,
                    grossTotalPrice: v.grossTotalPrice,
                    document: v.document,
                    isMultiSession: v.isMultiSession,
                    category: v.category,
                    ...(serviceProvider ? { serviceProviderId: serviceProvider.id } : {})
                  };
                }
                return item;
              }),
              treatmentDone: assessment.treatmentDone.map((item) => {
                if (item.service.id === v.id) {
                  return {
                    ...item,
                    service: {
                      id: v.id,
                      name: v.name,
                      teeth: v.teeth,
                      grossTotalPrice: v.grossTotalPrice,
                      document: v.document,
                      isMultiSession: v.isMultiSession,
                      category: v.category,
                      ...(serviceProvider ? { serviceProviderId: serviceProvider.id } : {})
                    }
                  };
                }
                return item;
              })
            });
          }}
          handleRemoveService={(v, treatmentDone) => {
            setAssessment({
              ...assessment,
              dentalServices: v,
              treatmentDone
            });
          }}
        />
      )}

      {component === SORT_ORDER.XRAY_SERVICES && (
        <XrayServices
          intraOral={assessment.intraOral}
          xrayServices={assessment?.xrayServices || []}
          handleAddService={(v) => {
            setAssessment({
              ...assessment,
              xrayServices: [
                ...assessment.xrayServices,
                {
                  id: v.id,
                  name: v.name,
                  note: "",
                  teeth: [],
                  grossTotalPrice: v.grossTotalPrice,
                  document: v.document,
                  productisableId: v.productisableId || v?.labTestGroup?.id,
                  category: v.category
                }
              ]
            });
          }}
          handleUpdateService={(v) => {
            setAssessment({
              ...assessment,
              xrayServices: assessment.xrayServices.map((item) => {
                if (item.id === v.id) {
                  return {
                    id: v.id,
                    name: v.name,
                    note: v.note,
                    teeth: v.teeth,
                    grossTotalPrice: v.grossTotalPrice,
                    document: v.document,
                    productisableId: v.productisableId || v?.labTestGroup?.id,
                    category: v.category
                  };
                }
                return item;
              })
            });
          }}
          handleRemoveService={(updatedServices) => {
            setAssessment({
              ...assessment,
              xrayServices: updatedServices
            });
          }}
        />
      )}

      {component === SORT_ORDER.DIAGNOSIS && (
        <div>
          <DiagnosisWithTreeView
            label={assessmentLabels.diagnosis}
            onChange={(v) => {
              const newV = {
                diagnosis: v.code ? `${v.code} ${v.title}` : v.title,
                icdCode: v.code || ""
              };
              setAssessment({ ...assessment, diagnosis: [...assessment.diagnosis, newV] });
            }}
          />
          <Box className={styles.assessmentRow}>
            <div className={styles.assessmentLabel} />
            <Box className={styles.assessmentField} display="flex" alignItems="center" width="100%">
              <Stack gap={1} width="100%">
                <DiagnosisList setAssessment={setAssessment} assessment={assessment} />
              </Stack>
            </Box>
          </Box>
        </div>
      )}

      {component === SORT_ORDER.PRESENT_HEALTH_STATUS && (
        <PresentHealthStatus
          assessmentPresentHealthStatus={assessment?.presentHealthStatus || ""}
          onChange={(v) => setAssessment({ ...assessment, presentHealthStatus: v })}
        />
      )}

      {component === SORT_ORDER.ATTACHMENTS && (
        <Attachment
          assessmentId={assessmentId}
          onfilesChange={(files) => {
            setAssessment({ ...assessment, attachments: files });
          }}
        />
      )}

      {component === SORT_ORDER.TABULAR && (
        <Box className={styles.assessmentRow}>
          <Box className={styles.assessmentLabel}>
            <Typography component="span" fontWeight="600">
              {tabular.title}
            </Typography>
          </Box>
          <Box className={styles.assessmentField}>
            <EditableTable
              maxColCount={10}
              onChange={(value) =>
                setAssessment({
                  ...assessment,
                  tabularComponent: { ...assessment.tabularComponent, ...value }
                })
              }
              hideControls
              hideLabel
              initValue={tabular.initValue}
            />
          </Box>
        </Box>
      )}

      {component === SORT_ORDER.TREATMENT_PLAN && (
        <TreatmentPlan
          assessmentTreatmentPlan={assessment?.treatmentPlan}
          onChange={(v) => setAssessment({ ...assessment, treatmentPlan: v })}
        />
      )}

      {component === SORT_ORDER.TREATMENT_GIVEN && (
        <TreatmentGiven
          assessmentTreatmentGiven={assessment?.treatmentGiven}
          onChange={(v) => setAssessment({ ...assessment, treatmentGiven: v })}
        />
      )}
      {component === SORT_ORDER.TREATMENT_DONE && (
        <TreatmentDone
          treatmentDone={assessment?.treatmentDone}
          onChange={(v) => {
            setAssessment({ ...assessment, treatmentDone: v });
          }}
        />
      )}

      {component === SORT_ORDER.MEDICATION && (
        <Medication
          medication={assessment?.medication}
          spID={spID}
          onChange={(v) => {
            setAssessment({ ...assessment, medication: v });
          }}
          isOpd
        />
      )}

      {component === SORT_ORDER.PRODUCT_MEDICATION && (
        <ProductMedication
          productMedication={assessment?.productMedication}
          onChange={(v) => {
            setAssessment({ ...assessment, productMedication: v });
          }}
        />
      )}

      {component === SORT_ORDER.FOLLOWUP && (
        <FollowUp
          assessmentFollowUp={assessment.followup}
          onChange={(updatedValues) => {
            setAssessment({
              ...assessment,
              followup: { ...assessment.followup, ...updatedValues }
            });
          }}
        />
      )}

      {component === SORT_ORDER.ADVICE && (
        <Advice
          advice={assessment.advice}
          onChange={(advice) => setAssessment({ ...assessment, advice })}
        />
      )}

      {component === SORT_ORDER.OTHERS && (
        <Others
          assessmentOthers={assessment.others}
          onChange={(v) => {
            setAssessment({ ...assessment, others: v });
          }}
        />
      )}

      {component === SORT_ORDER.DERMASCOPY && (
        <Dermascopy
          assessment={assessment}
          onChange={(value) => setAssessment({ ...assessment, dermascopy: value })}
        />
      )}

      {component === SORT_ORDER.OTHER1 && (
        <Other1
          assessment={assessment}
          onChange={(value) => setAssessment({ ...assessment, other1: value })}
        />
      )}

      {component === SORT_ORDER.OTHER2 && (
        <Other2
          assessment={assessment}
          onChange={(value) => setAssessment({ ...assessment, other2: value })}
        />
      )}

      {component === SORT_ORDER.OTHER3 && (
        <Other3
          assessment={assessment}
          onChange={(value) => setAssessment({ ...assessment, other3: value })}
        />
      )}

      {component === SORT_ORDER.SKIN_ANALYSIS && (
        <SkinAnalysis
          assessment={assessment}
          onChange={(value: SkinAnalysisAttribute[]) =>
            setAssessment({ ...assessment, skinAnalysis: value })
          }
        />
      )}
    </>
  );
};

export default OPDComponents;
